import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Save, CalendarClock, Loader2, Cloud, Sun, CloudRain, CloudSnow, CloudFog, CloudDrizzle, CloudLightning, Fuel, ReceiptText, Route, AlertTriangle, UtensilsCrossed, BedDouble } from "lucide-react";

const ICONS = { sun: Sun, cloud: Cloud, "cloud-sun": Cloud, "cloud-rain": CloudRain, "cloud-snow": CloudSnow, "cloud-fog": CloudFog, "cloud-drizzle": CloudDrizzle, "cloud-lightning": CloudLightning };

export default function PianificazioneBlock({ cid, cliente, onSaved, restricted = false }) {
  const [data, setData] = useState(null);
  const [squadre, setSquadre] = useState([]);
  const [saving, setSaving] = useState(false);
  const [trasferta, setTrasferta] = useState(null);
  const [trasfertaLoading, setTrasfertaLoading] = useState(false);

  const loadTrasferta = async (date = "") => {
    setTrasfertaLoading(true);
    try {
      const params = date ? `?date=${encodeURIComponent(date.split("T")[0])}` : "";
      const { data } = await api.get(`/clienti/${cid}/trasferta${params}`);
      setTrasferta(data);
    } catch (err) {
      // 400 = missing citta_base
      setTrasferta({ ok: false, error: err?.response?.data?.detail || "Errore" });
    } finally { setTrasfertaLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [p, s] = await Promise.all([
          api.get(`/clienti/${cid}/pianificazione`),
          api.get("/squadre"),
        ]);
        setData(p.data);
        setSquadre(s.data);
        loadTrasferta(p.data.data_inizio || "");
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, [cid]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  // Holiday/weekend warning helpers
  const [holidaysMap, setHolidaysMap] = useState({});
  useEffect(() => {
    if (!data?.data_inizio) return;
    const yr = parseInt((data.data_inizio || "").slice(0, 4), 10);
    if (!yr || holidaysMap[yr]) return;
    api.get(`/holidays/it?year=${yr}`).then((r) => {
      setHolidaysMap((m) => ({ ...m, [yr]: r.data.holidays || {} }));
    }).catch(() => {});
  }, [data?.data_inizio, holidaysMap]);

  const dayWarning = (() => {
    const di = (data?.data_inizio || "").slice(0, 10);
    if (!di) return null;
    const yr = parseInt(di.slice(0, 4), 10);
    const holidays = holidaysMap[yr] || {};
    const dt = new Date(di + "T12:00:00");
    const dow = dt.getDay(); // Sun=0 .. Sat=6
    if (holidays[di]) return { type: "holiday", label: holidays[di] };
    if (dow === 0 || dow === 6) return { type: "weekend", label: dow === 0 ? "Domenica" : "Sabato" };
    return null;
  })();

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/clienti/${cid}/pianificazione`, {
        pannelli: data.pannelli || "",
        batteria: data.batteria || "",
        inverter: data.inverter || "",
        squadra_id: data.squadra_id || "",
        data_inizio: data.data_inizio || "",
        durata_ore: Number(data.durata_ore) || 4,
        note: data.note || "",
        vitto: Math.max(0, Number(data.vitto) || 0),
        alloggio: Math.max(0, Number(data.alloggio) || 0),
      });
      toast.success("Pianificazione salvata · visibile nel calendario");
      if (onSaved) onSaved();
      // refresh costi/meteo (date may have changed)
      loadTrasferta(data.data_inizio || "");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>;

  return (
    <form onSubmit={save} className="space-y-5" data-testid="pianificazione-form">
      <div className="card-shell p-6">
        <div className="flex items-center gap-2 mb-4">
          <CalendarClock className="w-5 h-5" style={{ color: "var(--primary)" }} />
          <h2 className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>Pianificazione installazione</h2>
        </div>

        {/* Dati cliente readonly */}
        <div className="p-4 rounded-md mb-5" style={{ background: "var(--surface-muted)" }}>
          <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>Dati cliente</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-1 text-sm">
            <div><strong>Nome:</strong> {cliente.nome} {cliente.cognome_ragione_sociale}</div>
            <div><strong>Tel:</strong> {cliente.telefono || "—"}</div>
            <div><strong>Email:</strong> {cliente.email || "—"}</div>
            <div className="md:col-span-2"><strong>Indirizzo:</strong> {cliente.indirizzo || "—"}, {cliente.cap || ""} {cliente.citta || "—"} {cliente.provincia && `(${cliente.provincia})`}</div>
            <div><strong>Commessa:</strong> {cliente.commessa?.name || "—"}</div>
          </div>
        </div>

        <fieldset disabled={restricted} className="min-w-0 border-0 p-0 m-0">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Pannelli" value={data.pannelli} onChange={(v) => set("pannelli", v)} testid="pian-pannelli" placeholder="Es. LG NeON R 380W x 20" />
          <Field label="Batteria" value={data.batteria} onChange={(v) => set("batteria", v)} testid="pian-batteria" placeholder="Es. Tesla Powerwall 13.5 kWh" />
          <Field label="Inverter" value={data.inverter} onChange={(v) => set("inverter", v)} testid="pian-inverter" placeholder="Es. SolarEdge 6 kW" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Squadra assegnata</label>
            <select value={data.squadra_id || ""} onChange={(e) => set("squadra_id", e.target.value)}
              className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
              style={{ borderColor: "var(--border)", background: "#fff" }}
              data-testid="pian-squadra">
              <option value="">— Non assegnata —</option>
              {squadre.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Giorno e ora inizio *</label>
            <input type="datetime-local" value={data.data_inizio ? data.data_inizio.slice(0, 16) : ""}
              onChange={(e) => set("data_inizio", e.target.value)}
              className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
              style={{ borderColor: "var(--border)", background: "#fff" }}
              data-testid="pian-data-inizio" />
            {dayWarning && (
              <div
                className="mt-2 flex items-start gap-2 p-2 rounded text-xs"
                style={{ background: "#FEF3C7", border: "1px solid #FCD34D", color: "#92400E" }}
                data-testid="pian-day-warning"
              >
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  <b>{dayWarning.type === "holiday" ? `Festività: ${dayWarning.label}` : `Giorno non lavorativo (${dayWarning.label})`}</b>
                  {" "}— Conferma con la squadra prima di procedere.
                </span>
              </div>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Durata stimata (ore)</label>
            <input type="number" step="0.5" min="0.5" value={data.durata_ore || 4}
              onChange={(e) => set("durata_ore", e.target.value)}
              className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
              style={{ borderColor: "var(--border)", background: "#fff" }}
              data-testid="pian-durata" />
          </div>
        </div>

        <div className="mt-4">
          <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Note</label>
          <textarea rows={3} value={data.note || ""} onChange={(e) => set("note", e.target.value)}
            className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
            style={{ borderColor: "var(--border)", background: "#fff" }}
            placeholder="Accessi, difficoltà previste, contatti in loco…"
            data-testid="pian-note" />
        </div>
        </fieldset>

        {/* Spese custom (vitto + alloggio) — sommate ai costi trasferta */}
        {!restricted && (
        <div className="mt-5 p-4 rounded-md" style={{ background: "var(--surface-muted)" }}>
          <div className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: "var(--text-muted)" }}>
            Spese extra trasferta (€) · sommate al totale
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <MoneyField
              icon={UtensilsCrossed} label="Vitto"
              value={data.vitto} onChange={(v) => set("vitto", v)}
              testid="pian-vitto"
            />
            <MoneyField
              icon={BedDouble} label="Alloggio"
              value={data.alloggio} onChange={(v) => set("alloggio", v)}
              testid="pian-alloggio"
            />
          </div>
        </div>
        )}

        {!restricted && (
        <div className="flex justify-end mt-5">
          <button type="submit" disabled={saving} className="btn-primary disabled:opacity-60" data-testid="pian-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salva pianificazione
          </button>
        </div>
        )}
      </div>

      {/* Costi trasferta + meteo */}
      <div className="card-shell p-6" data-testid="pian-trasferta-block">
        <div className="flex items-center gap-2 mb-4">
          <Route className="w-5 h-5" style={{ color: "var(--primary)" }} />
          <h3 className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>{restricted ? "Meteo" : "Costi trasferta e meteo"}</h3>
          {trasfertaLoading && <Loader2 className="w-4 h-4 animate-spin" />}
        </div>

        {!trasferta ? (
          <div className="text-sm" style={{ color: "var(--text-muted)" }}>Caricamento…</div>
        ) : !trasferta.ok ? (
          <div className="p-4 rounded-md text-sm" style={{ background: "#FFF8E1", color: "#7A4500" }} data-testid="pian-trasferta-err">
            <AlertTriangle className="w-4 h-4 inline mr-1" />
            {trasferta.error || "Configurazione mancante"}.
            {(trasferta.error || "").includes("Città base") && <> Vai in <strong>Impostazioni → Trasferta</strong>.</>}
          </div>
        ) : (
          <>
            {!restricted && trasferta.costi && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <KPI label="Distanza" value={`${trasferta.costi.distanza_totale_km} km`} sub={trasferta.costi.andata_ritorno ? "andata + ritorno" : "solo andata"} testid="pian-kpi-km" />
              <KPI label="Carburante" value={`€ ${trasferta.costi.costo_carburante.toFixed(2)}`} sub={`${trasferta.costi.litri_carburante} L`} icon={Fuel} testid="pian-kpi-fuel" />
              <KPI label="Caselli stim." value={`€ ${trasferta.costi.costo_caselli.toFixed(2)}`} sub={`${trasferta.costi.km_autostrada_stimati} km autostrada`} icon={ReceiptText} testid="pian-kpi-toll" />
              <KPI label="Totale spesa" value={`€ ${trasferta.costi.totale.toFixed(2)}`} sub="da addebitare al cliente" highlight testid="pian-kpi-total" />
            </div>
            )}
            {!restricted && trasferta.costi && (trasferta.costi.vitto > 0 || trasferta.costi.alloggio > 0) && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4" data-testid="pian-kpi-extras">
                <KPI label="Vitto" value={`€ ${(trasferta.costi.vitto || 0).toFixed(2)}`} sub="spesa custom" icon={UtensilsCrossed} testid="pian-kpi-vitto" />
                <KPI label="Alloggio" value={`€ ${(trasferta.costi.alloggio || 0).toFixed(2)}`} sub="spesa custom" icon={BedDouble} testid="pian-kpi-alloggio" />
                <KPI label="Tot. extra" value={`€ ${(trasferta.costi.costi_extra || 0).toFixed(2)}`} sub="vitto + alloggio" testid="pian-kpi-extras-total" />
              </div>
            )}
            <div className="text-xs" style={{ color: "var(--text-muted)" }}>
              Da <strong>{trasferta.citta_base?.label}</strong> a <strong>{trasferta.cliente_geo?.label}</strong>
            </div>

            <div className={`${restricted ? "" : "mt-4 pt-4 border-t"}`} style={restricted ? undefined : { borderColor: "var(--border)" }}>
              <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>
                Meteo previsto {data?.data_inizio ? `· ${data.data_inizio.slice(0, 10)}` : ""}
              </div>
              {trasferta.meteo ? (
                <MeteoCard meteo={trasferta.meteo} />
              ) : (
                <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                  {data?.data_inizio
                    ? "Previsioni non disponibili (data oltre 16 giorni o errore API)"
                    : "Imposta giorno e ora inizio per vedere il meteo"}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </form>
  );
}

function KPI({ label, value, sub, icon: Icon, highlight, testid }) {
  return (
    <div className="p-3 rounded-md" style={{ background: highlight ? "var(--primary)" : "var(--surface-muted)", color: highlight ? "#fff" : "inherit" }} data-testid={testid}>
      <div className="text-[10px] font-semibold uppercase tracking-wider opacity-80 inline-flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className="text-lg font-extrabold" style={{ fontFamily: "Manrope" }}>{value}</div>
      {sub && <div className="text-[11px] opacity-75">{sub}</div>}
    </div>
  );
}

function MeteoCard({ meteo }) {
  const Icon = ICONS[meteo.icon] || Cloud;
  return (
    <div className="flex items-center gap-4 p-4 rounded-md" style={{ background: "linear-gradient(135deg, #DBEAFE, #E0F2FE)" }} data-testid="pian-meteo-card">
      <Icon className="w-12 h-12" style={{ color: "#0369A1" }} />
      <div className="flex-1">
        <div className="font-bold text-base">{meteo.label}</div>
        <div className="text-xs" style={{ color: "var(--text-muted)" }}>
          Min {meteo.temp_min}°C · Max {meteo.temp_max}°C
          {meteo.precip_prob != null && <> · Pioggia {meteo.precip_prob}%</>}
          {meteo.wind_max != null && <> · Vento max {meteo.wind_max} km/h</>}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, placeholder }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{label}</label>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
        style={{ borderColor: "var(--border)", background: "#fff" }}
        data-testid={testid} />
    </div>
  );
}

function MoneyField({ icon: Icon, label, value, onChange, testid }) {
  // Input numerico: ammette solo cifre e un punto/virgola decimale
  const handle = (e) => {
    let v = e.target.value.replace(",", ".");
    // Allow empty (clearing the field)
    if (v === "") { onChange(""); return; }
    // Match optional integer + optional decimals
    if (/^\d*\.?\d{0,2}$/.test(v)) onChange(v);
  };
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider inline-flex items-center gap-1" style={{ color: "var(--text-muted)" }}>
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </label>
      <div className="mt-1 relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold" style={{ color: "var(--text-muted)" }}>€</span>
        <input
          type="text" inputMode="decimal" pattern="[0-9]*[.,]?[0-9]{0,2}"
          value={value === 0 ? "0" : (value ?? "")}
          onChange={handle}
          placeholder="0.00"
          className="w-full pl-7 pr-3 py-2 text-sm rounded-md border outline-none"
          style={{ borderColor: "var(--border)", background: "#fff" }}
          data-testid={testid}
        />
      </div>
    </div>
  );
}
